import { supabase } from '@/integrations/supabase/client';

export class AnalyticsCleanup {

  /**
   * Detecta se a aplicação está rodando como PWA
   */
  static isPWA(): boolean {
    // Verifica se está no modo standalone (PWA instalado)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    // Verifica se foi iniciado do homescreen
    const isFromHomescreen = (window.navigator as any).standalone === true;
    
    // Verifica se está em um iframe (algumas PWAs)
    const isInIframe = window.self !== window.top;
    
    return isStandalone || isFromHomescreen || isInIframe;
  }

  /**
   * Limpeza automática de analytics no login
   */
  static async performLoginCleanup(userId: string): Promise<void> {
    try {
      console.log('🧹 Iniciando limpeza automática de analytics no login');
      
      // Obter todas as empresas do usuário
      const { data: userCompanies } = await supabase
        .from('user_companies')
        .select('company_id, companies(created_at)')
        .eq('user_id', userId);

      if (!userCompanies) return;

      for (const membership of userCompanies) {
        const companyId = membership.company_id;
        const companyCreatedAt = (membership as any).companies?.created_at;
        
        // Verificar se é empresa nova (< 24h)
        if (companyCreatedAt) {
          const companyAge = Date.now() - new Date(companyCreatedAt).getTime();
          const ageHours = companyAge / (1000 * 60 * 60);
          
          if (ageHours < 24) {
            console.log(`🧹 Empresa nova detectada (${ageHours.toFixed(1)}h), resetando analytics:`, companyId);
            this.clearCompanyAnalytics(companyId);
            this.generateRealisticInitialData(companyId);
          }
        }
        
        // Se for PWA, fazer limpeza mais agressiva
        if (this.isPWA()) {
          console.log('🧹 PWA detectado, limpeza agressiva para empresa:', companyId);
          this.clearCompanyAnalytics(companyId);
          this.generateRealisticInitialData(companyId);
        }
      }
      
      // Limpar dados antigos em geral
      this.cleanOldAnalytics();
      
      console.log('✅ Limpeza automática de analytics concluída');
    } catch (error) {
      console.error('❌ Erro na limpeza automática de analytics:', error);
    }
  }
  
  /**
   * Limpa todos os dados de analytics de uma empresa específica
   */
  static clearCompanyAnalytics(companyId: string): void {
    try {
      // Remover todos os dados locais da empresa
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`analytics_${companyId}_`)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      console.log(`🧹 Analytics limpos para empresa ${companyId}: ${keysToRemove.length} chaves removidas`);
    } catch (error) {
      console.error('Erro ao limpar analytics:', error);
    }
  }

  /**
   * Detecta se o usuário atual é um administrador da empresa
   */
  static async isCurrentUserAdmin(companyId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return false;

      // Verificar se é membro da empresa
      const { data: userCompany } = await supabase
        .from('user_companies')
        .select('role')
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .single();

      // É admin se tem qualquer papel na empresa ou é super admin
      return Boolean(userCompany?.role) || await this.isSuperAdmin(user.id);
    } catch (error) {
      console.error('Erro ao verificar se usuário é admin:', error);
      return false;
    }
  }

  /**
   * Verifica se o usuário é super admin
   */
  static async isSuperAdmin(userId: string): Promise<boolean> {
    try {
      const { data: user } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      return user?.role === 'super_admin';
    } catch (error) {
      console.error('Erro ao verificar super admin:', error);
      return false;
    }
  }

  /**
   * Filtra eventos para incluir apenas visitantes públicos
   */
  static filterPublicVisitorEvents(events: any[]): any[] {
    return events.filter(event => {
      // Filtrar eventos que não são de administradores
      // Por enquanto, assumimos que eventos com user_agent específicos são de admins
      const userAgent = event.user_agent || '';
      const isLikelyAdmin = event.event_data?.is_admin_session === true;
      
      return !isLikelyAdmin;
    });
  }

  /**
   * Gera dados iniciais realistas para uma empresa nova
   */
  static generateRealisticInitialData(companyId: string): void {
    try {
      const today = new Date().toDateString();
      const key = `analytics_${companyId}_${today}`;
      
      // Começar com dados vazios para empresas novas
      const initialEvents: any[] = [];
      
      localStorage.setItem(key, JSON.stringify(initialEvents));
      
      console.log(`✨ Dados iniciais realistas gerados para empresa ${companyId}`);
    } catch (error) {
      console.error('Erro ao gerar dados iniciais:', error);
    }
  }

  /**
   * Valida se os dados de analytics são realistas
   */
  static validateAnalyticsData(events: any[], companyCreatedAt?: string): {
    isValid: boolean;
    issues: string[];
    recommendations: string[];
  } {
    const issues: string[] = [];
    const recommendations: string[] = [];
    
    // Verificar se há muitos eventos para uma empresa nova
    if (companyCreatedAt) {
      const companyAge = Date.now() - new Date(companyCreatedAt).getTime();
      const ageDays = companyAge / (1000 * 60 * 60 * 24);
      
      if (ageDays < 1 && events.length > 10) {
        issues.push(`Empresa criada há ${ageDays.toFixed(1)} dias mas já tem ${events.length} eventos`);
        recommendations.push('Considere resetar os analytics para dados mais realistas');
      }
    }
    
    // Verificar padrões suspeitos
    const sessionStarts = events.filter(e => e.event_type === 'session_start');
    const uniqueUserAgents = new Set(events.map(e => e.user_agent)).size;
    
    if (sessionStarts.length > 0 && uniqueUserAgents === 1) {
      issues.push('Todos os eventos têm o mesmo user-agent (provável teste/admin)');
      recommendations.push('Remover eventos de teste antes do lançamento');
    }
    
    return {
      isValid: issues.length === 0,
      issues,
      recommendations
    };
  }

  /**
   * Cria uma sessão única para evitar duplicação
   */
  static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Debounce para eventos repetitivos
   */
  static createEventDebouncer(delay: number = 1000) {
    const lastEventTimes = new Map<string, number>();
    
    return (eventKey: string, callback: () => void) => {
      const now = Date.now();
      const lastTime = lastEventTimes.get(eventKey) || 0;
      
      if (now - lastTime > delay) {
        lastEventTimes.set(eventKey, now);
        callback();
      }
    };
  }

  /**
   * Limpa dados de analytics antigos (mais de 30 dias)
   */
  static cleanOldAnalytics(): void {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('analytics_')) {
          // Extrair data da chave
          const parts = key.split('_');
          if (parts.length >= 3) {
            const dateStr = parts.slice(2).join('_');
            const eventDate = new Date(dateStr);
            
            if (eventDate < thirtyDaysAgo) {
              keysToRemove.push(key);
            }
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`🧹 Removidos ${keysToRemove.length} registros antigos de analytics`);
      }
    } catch (error) {
      console.error('Erro ao limpar dados antigos:', error);
    }
  }
}